import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Container from '../common/Container';
import Button from '../common/Button';
import Footer from '../layout/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../hooks/useTheme';
import { Elements } from '@stripe/react-stripe-js';
import PaymentForm, { stripePromise } from '../payment/PaymentForm';
import { getCurrentUser, signOut, getAuthTokens } from '../../services/authService';
import { getSubscriptionChoice, hasOrganization, getUserRole } from '../../utils/organizationUtils';
import { isDownloadReady, openDownload } from '../../config/download';

const ManageSubscriptionWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ManageSubscriptionContent = styled.div`
  flex: 1;
  padding: 6rem 0;
  background: var(--background-primary);
`;

const FormContainer = styled.div`
  max-width: 650px;
  margin: 0 auto;
  padding: 2.5rem;
  background: var(--card-background);
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  transition: all 0.3s ease;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 2rem;
  color: var(--text-primary);
  font-size: 1.75rem;
  font-weight: 700;

  @media (max-width: 576px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  color: var(--text-secondary);
  font-size: 1.1rem;

  @media (max-width: 576px) {
    font-size: 1rem;
  }
`;

const SubscriptionInfo = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
`;

const SubscriptionDetail = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: var(--text-secondary);

    svg {
      color: var(--accent-primary);
      flex-shrink: 0;
      margin-top: 0.25rem;
    }
  }
`;

const InputGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-primary);

  @media (prefers-color-scheme: dark) {
    color: #f5f5f5;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 1rem;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(var(--accent-primary-rgb), 0.2);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const ErrorMessage = styled.div`
  color: #e53935;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const API_URL = 'https://xoxl3mvo9e.execute-api.us-east-1.amazonaws.com/prod';

const ManageSubscription = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [subscriptionChoice, setSubscriptionChoice] = useState(null);
  const [hasOrg, setHasOrg] = useState(false);
  const [userRole, setUserRoleState] = useState(null);
  const [organizationName, setOrganizationName] = useState('');
  const [organizationNameError, setOrganizationNameError] = useState('');
  const [showOrgNameInput, setShowOrgNameInput] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const navigate = useNavigate();
  const { theme } = useTheme();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);

        // Check if user has an organization
        const orgExists = hasOrganization();
        setHasOrg(orgExists);

        // Get user role
        const role = getUserRole();
        setUserRoleState(role);

        // Get cached subscription choice
        const cachedSubscription = getSubscriptionChoice();
        setSubscriptionChoice(cachedSubscription);

        // Get organization name from localStorage if available
        const savedOrgName = localStorage.getItem('organizationName');
        if (savedOrgName) {
          setOrganizationName(savedOrgName);
        } else if (userData?.attributes?.['custom:organization_name']) {
          // If not in localStorage but in user attributes, use that
          setOrganizationName(userData.attributes['custom:organization_name']);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setLoading(false);
      }
    };

    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    fetchUserData();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSubscribe = () => {
    setShowOrgNameInput(true);
  };

  const handleContinueToPayment = () => {
    // Validate organization name
    if (!organizationName.trim()) {
      setOrganizationNameError('Organization name is required');
      return;
    }

    // Clear any previous errors
    setOrganizationNameError('');

    // Show payment form
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = (subscriptionId) => {
    // After successful payment, update the UI to show the user has a subscription
    setHasOrg(true);
    setShowPaymentForm(false);
    setShowSuccessScreen(true);

    // Store the organization name in localStorage
    if (organizationName) {
      localStorage.setItem('organizationName', organizationName);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setPaymentError(errorMessage);
  };

  const openBillingPortal = async () => {
    setBillingLoading(true);
    setBillingError('');

    try {
      const tokens = getAuthTokens();
      if (!tokens || !tokens.idToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/payment/billing-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': tokens.idToken
        },
        body: JSON.stringify({
          returnUrl: window.location.href
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to open billing portal');
      }

      const data = await response.json();

      // Redirect to Stripe billing portal
      window.location.href = data.url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      setBillingError(error.message || 'Failed to open billing portal');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCancel = () => {
    openBillingPortal();
  };

  const handleUpgradeDowngrade = () => {
    openBillingPortal();
  };

  const handleLeaveOrganization = async () => {
    setLeaveLoading(true);
    setLeaveError('');

    try {
      const tokens = getAuthTokens();
      if (!tokens || !tokens.idToken) {
        throw new Error('Not authenticated');
      }

      // Get user's Cognito sub from the token
      const tokenPayload = JSON.parse(atob(tokens.idToken.split('.')[1]));
      const userId = tokenPayload.sub;

      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': tokens.idToken
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to leave organization');
      }

      // Clear local storage and sign out
      await signOut();
      navigate('/get-started');
    } catch (error) {
      console.error('Error leaving organization:', error);
      setLeaveError(error.message || 'Failed to leave organization');
      setShowLeaveConfirm(false);
    } finally {
      setLeaveLoading(false);
    }
  };

  if (loading) {
    return (
      <ManageSubscriptionWrapper>
        <ManageSubscriptionContent>
          <Container>
            <FormContainer>
              <Title>Loading...</Title>
            </FormContainer>
          </Container>
        </ManageSubscriptionContent>
        <Footer />
      </ManageSubscriptionWrapper>
    );
  }

  if (!user) {
    // Redirect to login if not authenticated
    navigate('/get-started');
    return null;
  }

  return (
    <ManageSubscriptionWrapper>
      <ManageSubscriptionContent>
        <Container>
          <FormContainer>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <Button
                variant="secondary"
                onClick={() => navigate(-1)}
                style={{ padding: '8px 16px' }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
              <div style={{ width: '80px' }}><img src="/signage-logo.png" alt="Signage Logo" style={{ width: '100%', height: 'auto' }} /></div>
            </div>

            <Title>Manage Your Subscription</Title>

            {showSuccessScreen ? (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    margin: '0 auto 1.5rem', 
                    borderRadius: '50%', 
                    background: 'rgba(67, 160, 71, 0.1)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                  }}>
                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '2rem', color: '#43a047' }} />
                  </div>
                  <Subtitle style={{ color: '#43a047', fontWeight: 'bold', fontSize: '1.5rem' }}>Thank You!</Subtitle>
                  <p style={{ 
                    marginBottom: '1.5rem', 
                    fontSize: '1.1rem', 
                    color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                  }}>
                    Your subscription has been successfully activated.
                  </p>
                  <p style={{ 
                    marginBottom: '2rem', 
                    fontSize: '1rem', 
                    color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                  }}>
                    Download the app to continue setting up your digital signage.
                  </p>
                </div>

                <Button
                  variant="primary"
                  fullWidth
                  disabled={!isDownloadReady()}
                  onClick={openDownload}
                  style={{ marginBottom: '1rem' }}
                >
                  {isDownloadReady() ? 'Download App' : 'Download App (Coming Soon)'}
                </Button>

                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    setShowSuccessScreen(false);
                  }}
                >
                  Go to Dashboard
                </Button>

                <p style={{ 
                  textAlign: 'center', 
                  marginTop: '1.5rem', 
                  fontSize: '0.875rem', 
                  color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                }}>
                  Need help? Contact our support team at support@signage-llc.com
                </p>
              </div>
            ) : showPaymentForm ? (
              <div>
                <Subtitle>Complete your subscription payment</Subtitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowPaymentForm(false);
                      setShowOrgNameInput(true);
                    }}
                    style={{ padding: '8px 16px' }}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                  </Button>
                  <div>
                    <strong>Organization:</strong> {organizationName}
                  </div>
                </div>
                {subscriptionChoice && (
                  <p style={{ 
                    textAlign: 'center', 
                    marginBottom: '1.5rem', 
                    fontSize: '0.95rem', 
                    color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                  }}>
                    {subscriptionChoice.isPromo
                      ? `2 days free, then $${subscriptionChoice.regularPrice}/month unless cancelled`
                      : `$${subscriptionChoice.price}/month`}
                  </p>
                )}

                {paymentError && (
                  <div className="error" style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    backgroundColor: 'rgba(229, 57, 53, 0.1)',
                    color: '#e53935'
                  }}>
                    {paymentError}
                  </div>
                )}

                <Elements stripe={stripePromise}>
                  <PaymentForm 
                    email={user?.attributes?.email || ''} 
                    organizationName={organizationName} 
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                </Elements>

                <p style={{ 
                  textAlign: 'center', 
                  marginTop: '1.5rem', 
                  fontSize: '0.875rem', 
                  color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                }}>
                  <FontAwesomeIcon icon={faCheck} style={{ marginRight: '5px' }} />
                  Your payment is secured with Stripe. We do not store your card details.
                </p>
              </div>
            ) : showOrgNameInput ? (
              <div>
                <Subtitle>Enter your organization name to continue</Subtitle>

                <div style={{ position: 'relative' }}>
                  <InputGroup>
                    <Label htmlFor="organizationName">Organization Name</Label>
                    <InputIcon>
                      <FontAwesomeIcon icon={faBuilding} />
                    </InputIcon>
                    <Input
                      type="text"
                      id="organizationName"
                      value={organizationName}
                      onChange={(e) => setOrganizationName(e.target.value)}
                      placeholder="Your Organization"
                    />
                    {organizationNameError && <ErrorMessage>{organizationNameError}</ErrorMessage>}
                  </InputGroup>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <Button
                    variant="secondary"
                    style={{ flex: 1 }}
                    onClick={() => setShowOrgNameInput(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    style={{ flex: 2 }}
                    onClick={handleContinueToPayment}
                  >
                    Continue to Payment
                  </Button>
                </div>

                <p style={{ 
                  textAlign: 'center', 
                  marginTop: '1.5rem', 
                  fontSize: '0.875rem', 
                  color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                }}>
                  Your organization name will be associated with your subscription.
                </p>
              </div>
            ) : (
              <>
                {hasOrg ? (
                  <>
                    {/* Show different UI based on user role */}
                    {userRole === 'Owner' ? (
                      <>
                        <Subtitle>Your subscription is active</Subtitle>
                        <SubscriptionInfo>
                          <SubscriptionDetail>
                            <strong>Plan:</strong>
                            <span>{subscriptionChoice?.name || 'Phase 1'}</span>
                          </SubscriptionDetail>
                          <SubscriptionDetail>
                            <strong>Price:</strong>
                            <span>${subscriptionChoice?.regularPrice || 200}/month</span>
                          </SubscriptionDetail>
                          <SubscriptionDetail>
                            <strong>Status:</strong>
                            <span style={{ color: 'var(--accent-primary)' }}>Active</span>
                          </SubscriptionDetail>
                          <SubscriptionDetail>
                            <strong>Role:</strong>
                            <span style={{ color: 'var(--accent-primary)' }}>Owner</span>
                          </SubscriptionDetail>
                        </SubscriptionInfo>

                        {subscriptionChoice && subscriptionChoice.features && (
                          <div>
                            <h3>Features included:</h3>
                            <FeatureList>
                              {subscriptionChoice.features.map((feature, idx) => (
                                <li key={idx}>
                                  <FontAwesomeIcon icon={faCheck} />
                                  {feature}
                                </li>
                              ))}
                            </FeatureList>
                          </div>
                        )}

                        <Button
                          variant="primary"
                          fullWidth
                          disabled={!isDownloadReady()}
                          onClick={openDownload}
                          style={{ marginBottom: '1rem' }}
                        >
                          {isDownloadReady() ? 'Download App' : 'Download App (Coming Soon)'}
                        </Button>

                        {billingError && (
                          <div style={{
                            marginBottom: '1rem',
                            padding: '0.75rem',
                            borderRadius: '8px',
                            textAlign: 'center',
                            fontSize: '0.875rem',
                            backgroundColor: 'rgba(229, 57, 53, 0.1)',
                            color: '#e53935'
                          }}>
                            {billingError}
                          </div>
                        )}

                        <ButtonGroup>
                          <Button
                            variant="secondary"
                            fullWidth
                            onClick={handleUpgradeDowngrade}
                            disabled={billingLoading}
                          >
                            {billingLoading ? 'Loading...' : 'Manage Billing'}
                          </Button>
                          <Button
                            variant="danger"
                            fullWidth
                            onClick={handleCancel}
                            disabled={billingLoading}
                          >
                            {billingLoading ? 'Loading...' : 'Cancel Subscription'}
                          </Button>
                        </ButtonGroup>
                      </>
                    ) : (
                      <>
                        {/* Admin or Staff view - show role info */}
                        <Subtitle>You are part of an organization</Subtitle>
                        <SubscriptionInfo>
                          <SubscriptionDetail>
                            <strong>Your Role:</strong>
                            <span style={{
                              color: userRole === 'Admin' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                              fontWeight: 'bold'
                            }}>
                              {userRole || 'Staff'}
                            </span>
                          </SubscriptionDetail>
                          <SubscriptionDetail>
                            <strong>Status:</strong>
                            <span style={{ color: 'var(--accent-primary)' }}>Active Member</span>
                          </SubscriptionDetail>
                        </SubscriptionInfo>

                        <p style={{
                          marginBottom: '1.5rem',
                          fontSize: '0.95rem',
                          color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)',
                          textAlign: 'center'
                        }}>
                          {userRole === 'Admin'
                            ? 'As an Admin, you have elevated permissions within the app.'
                            : 'As a Staff member, you can access the app and work on projects.'}
                        </p>

                        <Button
                          variant="primary"
                          fullWidth
                          disabled={!isDownloadReady()}
                          onClick={openDownload}
                          style={{ marginBottom: '1rem' }}
                        >
                          {isDownloadReady() ? 'Download App' : 'Download App (Coming Soon)'}
                        </Button>

                        {leaveError && (
                          <div style={{
                            marginBottom: '1rem',
                            padding: '0.75rem',
                            borderRadius: '8px',
                            textAlign: 'center',
                            fontSize: '0.875rem',
                            backgroundColor: 'rgba(229, 57, 53, 0.1)',
                            color: '#e53935'
                          }}>
                            {leaveError}
                          </div>
                        )}

                        {showLeaveConfirm ? (
                          <div style={{
                            padding: '1rem',
                            borderRadius: '8px',
                            backgroundColor: theme === 'dark' ? 'rgba(229, 57, 53, 0.1)' : 'rgba(229, 57, 53, 0.05)',
                            border: '1px solid rgba(229, 57, 53, 0.3)',
                            marginBottom: '1rem'
                          }}>
                            <p style={{
                              marginBottom: '1rem',
                              fontSize: '0.95rem',
                              color: theme === 'dark' ? '#f5f5f5' : 'var(--text-primary)',
                              textAlign: 'center'
                            }}>
                              Are you sure you want to leave this organization? You will lose access to all projects and data.
                            </p>
                            <ButtonGroup>
                              <Button
                                variant="secondary"
                                fullWidth
                                onClick={() => setShowLeaveConfirm(false)}
                                disabled={leaveLoading}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="danger"
                                fullWidth
                                onClick={handleLeaveOrganization}
                                disabled={leaveLoading}
                              >
                                {leaveLoading ? 'Leaving...' : 'Yes, Leave'}
                              </Button>
                            </ButtonGroup>
                          </div>
                        ) : (
                          <Button
                            variant="danger"
                            fullWidth
                            onClick={() => setShowLeaveConfirm(true)}
                            style={{ marginTop: '1rem' }}
                          >
                            Leave Organization
                          </Button>
                        )}

                        <p style={{
                          textAlign: 'center',
                          marginTop: '1rem',
                          fontSize: '0.875rem',
                          color: theme === 'dark' ? '#888' : 'var(--text-tertiary)'
                        }}>
                          Contact your organization owner to manage subscription settings.
                        </p>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Subtitle>You don't have an active subscription</Subtitle>
                    {subscriptionChoice && (
                      <div>
                        <h3>Selected plan: {subscriptionChoice.name}</h3>
                        <p style={{ 
                          marginBottom: '1.5rem', 
                          fontSize: '0.95rem', 
                          color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                        }}>
                          {subscriptionChoice.isPromo 
                            ? `$${subscriptionChoice.price} for the first 7 days, then $${subscriptionChoice.regularPrice}/month`
                            : `$${subscriptionChoice.price}/month`}
                        </p>
                        <FeatureList>
                          {subscriptionChoice.features.map((feature, idx) => (
                            <li key={idx}>
                              <FontAwesomeIcon icon={faCheck} />
                              {feature}
                            </li>
                          ))}
                        </FeatureList>
                        <Button
                          variant="primary"
                          fullWidth
                          onClick={handleSubscribe}
                        >
                          Subscribe Now
                        </Button>
                      </div>
                    )}
                  </>
                )}

                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <Button
                    variant="text"
                    onClick={handleSignOut}
                  >
                    Sign Out
                  </Button>
                </div>
              </>
            )}
          </FormContainer>
        </Container>
      </ManageSubscriptionContent>
      <Footer />
    </ManageSubscriptionWrapper>
  );
};

export default ManageSubscription;
